import '../index.less';
import { useEffect, useState, useRef, useContext } from 'react';
import Viewport from './viewport';
import StmodeInput from '../stmode-input';
import SystemSwitch from '../system-switch';
import { PythaEngineControl, PythaEventCMD, Primitive, PythaEventZoomAll, PythaEventName, DataChangeStruct, PythaEventObject, RenderItemType, PythaEvent, PythaEventDataChange, AxesPrimitive, PythaEventSetting, SETTING_KEY } from 'pytha2';
import { EngineContext, NavigationContext, RegisterContext } from '@/App';
import { findBlocksInProject, deleteBlockInGraphic, createBlock } from '@/declare/api/geometry/block';
import { findPrimitivesInProject, createPrimitiveMulti, deletePrimitivesInProject } from '@/declare/api/geometry/primitive';
import { message } from 'antd';
import opentype from 'opentype.js';
import { useParams, useLocation } from 'react-router-dom';
import fontPath from '@/store/fonts/GB2312.ttf';
import { PythaPluginStructure } from 'pytha2-structure';
import { EventGraphRedrawAxisNet } from '@/plugin/event-graph/EGRedrawAxisNet';
import { EventGraphDeleteAxisNet } from '@/plugin/event-graph/EGDeleteAxisNet';
import { getStoreysByProject } from '@/declare/api/project/storey';
import { EventGraphGenVerticalAxisLine } from '@/plugin/event-graph/EGGenVerticalAxisLine';
import { EventGraphDeleteVeritcalAxisLine } from '@/plugin/event-graph/EGDeleteVeritcalAxisLine';

import { Group } from "@/declare/interface/project/group"
import { addGroup, deleteGroup, getGroups } from '@/declare/api/geometry/group';
import { coreAxios } from '@/declare/api/axios';
import { createAxesMulti, deleteAxes, findAxesListInGraphic } from '@/declare/api/geometry/axes';
import { EGTest } from '@/plugin/event-graph/test/EGTest';
import { findFileByUuid } from '@/declare/api/project/file';
import { File } from '@/declare/interface/project/file';
import { Transfer2DisplayScale } from '@/utils/unit';
import { Matrix4 } from 'three';
import { AxisNetPrimitive } from '@/plugin/primitive/AxisNetPrimitive';
import { getSpecialPrimitiveLayerUuid } from '@/utils/layer-utils';
import { LayerType } from '@/declare/enums/layer-type';
import localStore from '@/store/local-store';
import { emitter } from '@/utils/mitt';
import { Emit_Event_Common_Tool } from '@/declare/enums/emit-event';
import ToolPanel from './tool-panel';
import { GroupParamType } from '@/declare/enums/group-param-type';
import { TOOL_TYPE } from '@/declare/enums/menu-type';
/**
 * 最大单次加载图元量
 */
export const maxLoadPrimitiveNum = 1000;
interface IProps {
    onSubscribe?: (event: PythaEvent, engine?: PythaEngineControl) => boolean;
}

export default function Draw(props: IProps) {

    /**
    * 当前图形 UUID，从路由参数中获取。
    */
    const { projectUuid, modelUuid, storeyUuid, sectionId } = useParams();
    /**
     * 引擎上下文，用于管理全局的引擎实例。
     */
    const engineContext = useContext(EngineContext);
    /**
     * 导航上下文
     */
    const navigateContext = useContext(NavigationContext);
    /**
     * 注册上下文
     */
    const registerContext = useContext(RegisterContext);
    /**
     * 控制初始化加载状态
     */
    const [loading, setLoading] = useState<boolean>(false);
    /**
     * 加载提示语
     */
    const [loadingTip, setLoadingTip] = useState<string>('');
    /**
     * 记录分批次加载图元的批数，用于判断是否结束加载中的状态
     */
    const countRef = useRef(0);
    /**
     * 路由
     */
    const location = useLocation();

    /**
     * 注册页签按钮
     */
    useEffect(() => {
        if (modelUuid == null || projectUuid == null) return;
        findFileByUuid(modelUuid).then(res => {
            let model: File = res.data;
            navigateContext.registTab({
                key: location.pathname,
                value: model?.name,
            })
            navigateContext.setActiveTabKey(location.pathname)
            let majorUuid = model.majorUuid;
            if (localStore.activeMajor != majorUuid) {
                // 跳转到专业
                emitter.emit(Emit_Event_Common_Tool.ACTIVE_MAJOR, majorUuid);
            }
        })

    }, [projectUuid, modelUuid])

    /**
    * 初始化模型数据
    * - 从后端获取与当前图形关联的模型数据。
    * - 将模型数据加载到引擎中。
    * 
    * @param engine Pytha 引擎实例。
    * @returns Promise 对象。
    */
    const initModels = async (engine: PythaEngineControl): Promise<any[]> => {

        return new Promise((resolve, reject) => {
            if (!modelUuid || !engine) {
                setLoading(false);
                reject([]);
            }
            setLoadingTip('正在下载图元');
            findPrimitivesInProject(projectUuid, { modelUuid: modelUuid }).then(res => {
                let jsonList: any[] = res.data || [];
                let axisNetJsonList: any[] = [];
                let jsonPrimitives = [];
                let loadCount = Math.ceil(jsonList.length / maxLoadPrimitiveNum);
                countRef.current = 0;
                if (loadCount === 0) {
                    setLoading(false); // 数据加载完成后隐藏 Loading
                }
                for (let i = 0; i < jsonList.length; i++) {
                    let json = jsonList[i].properties;
                    if (json.type == "AxisNet") {
                        axisNetJsonList.push(json);
                        continue;
                    }
                    jsonPrimitives.push(json);
                    if (jsonPrimitives.length >= maxLoadPrimitiveNum) {
                        setLoadingTip(`加载第 ${countRef.current + 1}/${loadCount} 批图元`);
                        engine.resolvePrimitives(jsonPrimitives);
                        countRef.current += 1;
                        if (countRef.current === loadCount) {
                            setLoading(false); // 数据加载完成后隐藏 Loading
                        }
                        jsonPrimitives = [];
                    }
                }
                // 最后一轮如果不到上限，需要再手动做一次加载
                if (jsonPrimitives.length > 0) {
                    setLoadingTip(`图元即将加载完成，请稍等`);
                    engine.resolvePrimitives(jsonPrimitives);
                    countRef.current += 1;
                    if (countRef.current === loadCount) {
                        setLoading(false); // 数据加载完成后隐藏 Loading
                    }
                }
                resolve(axisNetJsonList);
            }).catch(err => {
                console.log(err)
            })
        });
    }

    /**
     * 加载引擎参数 字体、块 图元
     * 
     * @param engine Pytha 引擎实例。
     */
    const registEngineData = async (engine: PythaEngineControl) => {
        if (!projectUuid) return;
        setLoading(true);
        try {
            // 注册加载文字
            if (engineContext.fontSource) {
                engine.registFont(engineContext.fontSource);
            } else {
                const font = await loadFont(fontPath);
                engineContext.setFontSource(font)
                engine.registFont(font);
            }
            // 设置引擎单位换算
            let unit = engineContext.unit;
            let unitRatio = Transfer2DisplayScale(unit);
            engine.registUnitRatio(1 / unitRatio);
            // 注册加载块
            const blocks = await loadBlocks(projectUuid) || [];
            engine.registBlocks(blocks);
            // 注册加载组
            const groups = await loadGroups(projectUuid) || [];
            engine.registGroups(groups);
            // 注册坐标列表
            const ucsList = await loadUcs(projectUuid, engine) || [];
            let UCSstatus = localStore.getToolPanelStatus(TOOL_TYPE.UCS);
            let activeUCS = UCSstatus?.activeUCS
            if (ucsList.some(item => item.uuid === activeUCS)) {
                const activeAxes = ucsList.find(item => item.uuid === activeUCS);
                if (activeAxes) {
                    // 通知引擎进行坐标系的切换
                    const pythaEvent = new PythaEventSetting();
                    pythaEvent.data = { key: SETTING_KEY.AXES, value: activeAxes };
                    engine.eventMediator?.emitEvent(pythaEvent);
                }
            }
            // 阻止来自编辑轴网页签的事件
            const path = location.pathname;
            if (path.includes("axis-net")) {
                setLoading(false);
                return;
            }
            const storeyRes = await getStoreysByProject(projectUuid);
            const storeys = storeyRes.data || [];

            const axisNetJsonList = await initModels(engine);
            const axisNetLayerUuid = await getSpecialPrimitiveLayerUuid(projectUuid, LayerType.AXIS_NET, engineContext, '#E54242');
            let layer = engine?.storage?.layerStore?.layers?.find(item => item.uuid === axisNetLayerUuid);

            let axisNetPrimitives = [];
            for (let json of axisNetJsonList) {
                let primitive = new Primitive();
                primitive.fromJson(json);
                const mat = primitive.worldModelMatrix;
                // 处理子图元
                let childJson = json['children'] || [];
                for (let child of childJson) {
                    let childPrimitive = new AxisNetPrimitive(child.type);
                    childPrimitive.fromJson(child);
                    // 给子图元应用父的矩阵
                    if (childPrimitive.localModelMatrix == null) childPrimitive.localModelMatrix = new Matrix4();
                    if (childPrimitive.worldModelMatrix == null) childPrimitive.worldModelMatrix = new Matrix4();
                    childPrimitive.localModelMatrix.premultiply(mat);
                    childPrimitive.worldModelMatrix.copy(childPrimitive.localModelMatrix);
                    if (childPrimitive.worldModelMatrixInverse == null) childPrimitive.worldModelMatrixInverse = new Matrix4();
                    childPrimitive.worldModelMatrixInverse.copy(childPrimitive.worldModelMatrix).invert();
                    // 绘图区的轴网图元 锁定
                    childPrimitive.lock = true;
                    childPrimitive.layerUuid = primitive.layerUuid;
                    childPrimitive.axisNetUuid = primitive.uuid;
                    if (childPrimitive.colorByLayer) {
                        // 直接设置为轴网图层的颜色
                        Object.keys(childPrimitive.renderItemDict).forEach(key => {
                            let item = childPrimitive.renderItemDict[key];
                            item.mat?.color.set(layer.color || '#E54242');
                        })
                    }
                    axisNetPrimitives.push(childPrimitive);
                }
            }

            let dataChange = new PythaEventDataChange({ add_primitives: axisNetPrimitives });
            engine.eventMediator.emitEvent(dataChange);

            // // 激活打散轴网图元
            // engine.eventMediator.emitEvent({
            //     cmd: 'CMD',
            //     data: EventGraphRedrawAxisNet.cmd,
            // });
            // let redrawEvent = new PythaEventObject();
            // redrawEvent.data = { axisNetPrimitives: axisNetPrimitives || [] };
            // engine.eventMediator?.emitEvent(redrawEvent);

            // 渲染竖向轴线
            engine.eventMediator.emitEvent({
                cmd: 'CMD',
                data: EventGraphGenVerticalAxisLine.cmd,
            });
            let vLineEvent = new PythaEventObject();
            vLineEvent.data = { storeys: storeys || [] };
            engine.eventMediator?.emitEvent(vLineEvent);

            // 激活ZA
            let zaEvent = new PythaEventZoomAll();
            zaEvent.data = {};
            engine.eventMediator?.emitEvent(zaEvent);

            setLoading(false);
        } catch (error) {
            console.log(error)
            setLoading(false);
        }
    }

    /**
     * 加载字体库
     * @param fontPath 
     * @returns 
     */
    const loadFont = (fontPath: string): Promise<opentype.Font> => {
        return new Promise((resolve, reject) => {
            setLoadingTip('正在加载引擎');
            opentype.load(fontPath, (err, font) => {
                if (err) {
                    reject(err); // 如果加载失败，调用 reject 并传入错误信息
                } else {
                    resolve(font); // 如果加载成功，调用 resolve 并传入字体对象
                }
            });
        });
    }

    /**
     * 加载块模板
     * @param projectUuid 
     * @returns 
     */
    const loadBlocks = (projectUuid: string): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            setLoadingTip('正在加载块模板');
            findBlocksInProject(projectUuid).then(res => {
                let jsonList = res.data || [];
                let blocks = [];
                for (let json of jsonList) {
                    let data = json.properties;
                    if (data != null) {
                        blocks.push(data);
                    }
                }
                resolve(blocks);
            }).catch(err => {
                reject(err);
            })
        })
    }

    const loadGroups = (projectUuid: string): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            setLoadingTip('正在加载组');
            getGroups(projectUuid).then(res => {
                let groups = res.data || [];
                resolve(groups);
            }).catch(err => {
                reject(err);
            })
        })
    }

    const loadUcs = (projectUuid: string, engine?: PythaEngineControl): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            setLoadingTip('正在加载组');
            findAxesListInGraphic(projectUuid).then(res => {
                // let Ucs = res.data || [];
                const jsonList: any[] = res.data || [];
                console.log("jsonList", jsonList)
                const axesList = [];
                for (const json of jsonList) {
                    const axes = new AxesPrimitive();
                    axes.fromJson(json.properties);
                    axesList.push(axes);
                }
                const wcsIndex = axesList.findIndex(axes => axes.isWcs);
                // 从原数组删除，再加到头部
                if (wcsIndex > 0) {
                    const wcs = axesList.splice(wcsIndex, 1)[0];
                    axesList.unshift(wcs);
                }
                resolve(axesList);
                let dataChange = new PythaEventDataChange({ add_axes: axesList });
                engine.eventMediator.emitEvent(dataChange);
            }).catch(err => {
                reject(err);
            })
        })
    }


    /**
     * 同步引擎内的图层
     */
    useEffect(() => {
        if (!engineContext?.engine) return;
        if (!engineContext?.engine?.storage) return;
        engineContext?.engine?.registLayers(engineContext.layers || []);
        engineContext?.engine?.registCurrentLayer(engineContext.currentLayerUuid);
    }, [engineContext.layers, engineContext.engine, engineContext.currentLayerUuid])

    /**
     * 组件挂载时初始化引擎
     * - 绑定引擎到视图容器。
     * - 加载初始模型数据。
     * - 监听引擎事件（如数据变更事件）。
     */
    useEffect(() => {
        const root = document.getElementById("viewport"); // 获取视图容器 DOM 元素
        if (!root) return;
        const engine = new PythaEngineControl(); // 创建引擎实例
        engine.switchOn(root); // 启动引擎并绑定到视图容器

        // 注册
        engine.storage.registerStore.registEventGraph(EventGraphRedrawAxisNet.cmd, EventGraphRedrawAxisNet);
        engine.storage.registerStore.registEventGraph(EventGraphDeleteAxisNet.cmd, EventGraphDeleteAxisNet);
        engine.storage.registerStore.registEventGraph(EventGraphGenVerticalAxisLine.cmd, EventGraphGenVerticalAxisLine);
        engine.storage.registerStore.registEventGraph(EventGraphDeleteVeritcalAxisLine.cmd, EventGraphDeleteVeritcalAxisLine);
        engine.storage.registerStore.registEventGraph(EGTest.cmd, EGTest);
        

        engineContext.setEngine(engine); // 保存引擎到全局上下文
        registEngineData(engine); // 注册引擎参数 文字、块
        // 监听引擎事件并显示通知
        const unsubscribe = engine.eventMediator.noticeHandler?.onNotice?.subscribe((sender, event) => {
            let res = true;
            if (props?.onSubscribe != null) {
                res = props.onSubscribe(event, engine);
            }
            if (!res) return;
            if (event.cmd === PythaEventName.DATA_CHANGE) {

                const path = location.pathname;
                if (path.includes("axis-net")) return; // 阻止来自编辑轴网页签的事件
                if (path.includes("section-detail")) return; // 阻止来自编辑截面页签的事件

                let data = event?.data as DataChangeStruct;
                if (!data) return;


                // 如果存在 add_axes，则上传到后端
                if (data.add_axes) {
                    createAxesMulti(projectUuid, data.add_axes.map(pri => {
                        let json = pri.toJson();
                        if (json != null) {
                            return json;
                        }
                        return null;
                    })).then(res => {
                    }).catch(err => {
                        console.error("上传失败:", err.response?.data || err.message);
                    })
                } else if (data.delete_axes) {
                    deleteAxes(data.delete_axes.uuid).then(res => { }).catch(err => {
                        console.error("上传失败:", err.response?.data || err.message);
                    })
                }

                // 如果存在 add_primitives，则上传到后端
                if (data.add_primitives && data.add_primitives.length > 0) {
                    if (!modelUuid) {
                        message.error("上传失败，模型不存在");
                        return;
                    }
                    let param = data.add_primitives.map(pri => {
                        if (!!(pri as AxisNetPrimitive)?.axisNetUuid) {
                            return null; // 阻止轴网图元的变化
                        }
                        let json = pri.toJson();
                        if (json != null) {
                            return json;
                        }
                        return null;
                    })?.filter(item => !!item) || [];
                    createPrimitiveMulti(param, { projectUuid: projectUuid, modelUuid: modelUuid }).then(res => {
                    }).catch(err => {
                        message.error("上传失败:" + err.response?.data || err.message, 1000);
                    });
                }
                if (data.delete_primitives && data.delete_primitives.length > 0) {//如果存在 delete_primitives，则从后端删除
                    let param = data.delete_primitives.map(pri => {
                        if (!!(pri as AxisNetPrimitive)?.axisNetUuid) {
                            return null; // 阻止轴网图元的变化
                        }
                        return pri.uuid;
                    })?.filter(item => !!item) || [];

                    deletePrimitivesInProject(param, projectUuid).then(res => {
                    }).catch(err => {
                        message.error("上传失败:" + (err.response?.data || err.message), 1000);
                    });
                }
                if (data.add_block_template != null) {
                    // 添加块
                    createBlock(data.add_block_template.toJson(), { projectUuid: projectUuid }).then(res => {

                    }).catch(err => {
                        message.error("上传块模板失败:" + (err.response?.data || err.message), 1000);
                    })
                }
                if (data.delete_block_template != null) {
                    // 删除块
                    deleteBlockInGraphic(data.delete_block_template.uuid).then(res => {

                    }).catch(err => {
                        message.error("删除块模板失败:" + (err.response?.data || err.message), 1000);
                    })
                }
                if (data.add_group != null) {
                    // 创建组
                    let group: Group = data.add_group;
                    group = {
                        ...group,
                        projectUuid: projectUuid,
                        paramType: GroupParamType.CUSTOMIZE,
                    }
                    addGroup(projectUuid, group).then(res => {

                    }).catch(err => {
                        message.error("创建组失败：" + err, 1000);
                    })
                }
                if (data.delete_group != null) {//如果存在 delete_primitives，则从后端删除
                    let group: Group = data.delete_group;
                    deleteGroup(group.uuid).then(res => {

                    }).catch(err => {
                        message.error("删除组失败：" + err, 1000);
                    })
                }
            }
        });

        // 注册结构引擎
        const structEnginePlugin = new PythaPluginStructure({ axios: coreAxios });
        engine.registPlugin(structEnginePlugin);


        return () => {
            unsubscribe?.();
            if (engine) {
                engine.switchOff();
                root.replaceChildren();
                engineContext.setEngine(null);
            }
        };
    }, [projectUuid, modelUuid, storeyUuid, sectionId]);

    return (
        <>
            <div className="viewport-wrap">
                <Viewport />
                <ToolPanel />
            </div>
            <div className="bottom">
                <StmodeInput />
                <SystemSwitch />
            </div>

            {/* <Modal
                open={loading}
                showHeader={false}
                showFooter={false}
                width={300}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px'
                }}>
                    <Spin spinning />
                    <div style={{ marginLeft: '10px' }}>{loadingTip + "..."}</div>
                </div>
            </Modal> */}
        </>
    )
}